import * as React from 'react';
import {
  createAutocomplete,
  AutocompleteState
} from '@algolia/autocomplete-core';
import { useSearchBox, UseSearchBoxProps } from 'react-instantsearch';
import { SearchListData } from '../search-list';

export type AutocompleteItem = {
  rawTitle?: string;
  title: string;
};

export type UseAutocompleteProps = {
  suggestions?: SearchListData['suggestions'];
} & UseSearchBoxProps;

const useAutocomplete = ({ suggestions, ...props }: UseAutocompleteProps) => {
  const { query: searchQuery, refine: setQuery } = useSearchBox(props);

  const [autocompleteState, setAutocompleteState] = React.useState<
    AutocompleteState<AutocompleteItem>
  >({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: searchQuery,
    activeItemId: null,
    status: 'idle'
  });

  const autocomplete = React.useMemo(
    () =>
      createAutocomplete<AutocompleteItem>({
        initialState: autocompleteState,
        onStateChange({ state }) {
          setAutocompleteState(state);
        },
        onSubmit({ state }) {
          setQuery(state.query);

          //Tracking
          const _paq = window._paq || [];
          _paq.push(['trackSiteSearch', state.query]);
          _paq.push([
            'setTrackerUrl',
            'https://piwik.net-federation.de/piwik.php'
          ]);
          _paq.push(['setSiteId', '148']);
        },
        onReset() {
          setQuery('');
        },
        getSources() {
          return [
            {
              sourceId: 'suggestions',
              getItems({ query }) {
                if (suggestions?.length === 0) return suggestions;
                const regex = new RegExp(`(${query})`, 'gi');

                const filteredSuggestions = suggestions!.filter(({ title }) =>
                  title.toLowerCase().includes(query.toLowerCase())
                );

                return filteredSuggestions.map((item) => ({
                  rawTitle: item.title,
                  title: item.title.replace(
                    regex,
                    '<mark class="ais-Highlight-highlighted">$1</mark>'
                  )
                }));
              },
              getItemInputValue({ item }) {
                return item.rawTitle!;
              },
              onSelect(state) {
                setQuery(state.item.rawTitle!);
              }
            }
          ];
        }
      }),
    []
  );

  return {
    autocomplete,
    autocompleteState
  };
};

export default useAutocomplete;
